import gsap from "gsap"
import { SplitText } from "gsap/SplitText"
import { useGSAP } from "@gsap/react"
import { forwardRef, useEffect, useMemo, useRef, type CSSProperties, type ForwardedRef, type MutableRefObject } from "react"
import { useSectionScrollStore } from "@/stores/sectionScrollStore"
import { useProjects } from "@/hooks/useProjects"
import { TECH_STACK_ICONS } from "@/lib/techStackIcons"

gsap.registerPlugin(SplitText)

const MARQUEE_FIELDS = [
  { top: "5%", left: "54%", width: "146vw", rotate: "12deg", rotateY: "10deg", perspective: "1500px", depth: 280, depthTilt: 14, scale: 0.8, opacity: 1, blur: "0px", duration: 57, chipHeight: "2.45rem", chipFont: "0.67rem", chipIcon: "1.36rem", direction: "forward" as const },
  { top: "51%", left: "57%", width: "174vw", rotate: "-7deg", rotateY: "8deg", perspective: "1320px", depth: 240, depthTilt: 12, scale: 1.02, opacity: 1, blur: "0px", duration: 45, chipHeight: "3rem", chipFont: "0.8rem", chipIcon: "1.72rem", direction: "forward" as const },
  { top: "79%", left: "49%", width: "198vw", rotate: "7deg", rotateY: "-5deg", perspective: "1120px", depth: 210, depthTilt: 10, scale: 1.24, opacity: 1, blur: "0px", duration: 39, chipHeight: "4.75rem", chipFont: "0.96rem", chipIcon: "2.08rem", direction: "reverse" as const },
]

function applyMarqueeRowTransforms(rows: NodeListOf<Element>) {
  rows.forEach((row, index) => {
    const config = MARQUEE_FIELDS[index]
    if (!config) return

    gsap.set(row, {
      xPercent: -50,
      rotateY: Number.parseFloat(config.rotateY),
      rotateZ: Number.parseFloat(config.rotate),
      scale: config.scale,
      transformPerspective: Number.parseFloat(config.perspective),
      filter: `blur(${config.blur})`,
      transformOrigin: "center center",
      transformStyle: "preserve-3d",
    })
  })
}

export const HomeSection = forwardRef<HTMLElement>(function HomeSection(_, ref) {
  const sectionRef = useRef<HTMLElement>(null)
  const prevActiveRef = useRef<boolean | null>(null)

  const isActive = useSectionScrollStore(
    (s) => s.sections[s.activeSectionIndex]?.type === "home",
  )

  const { data: projects } = useProjects()

  const allTechStack = useMemo(() => {
    if (!projects) return []
    const seen = new Set<string>()
    projects.forEach((project) => project.techStack.forEach((tech) => seen.add(tech)))
    return Array.from(seen).sort()
  }, [projects])

  const marqueeRows = useMemo(() => {
    if (!allTechStack.length) return MARQUEE_FIELDS.map(() => [])
    return MARQUEE_FIELDS.map((_, row) => {
      const offset = row * 4
      const rotated = [...allTechStack.slice(offset), ...allTechStack.slice(0, offset)]
      return [...rotated, ...rotated]
    })
  }, [allTechStack])

  const isActiveRef = useRef(isActive)
  useEffect(() => { isActiveRef.current = isActive }, [isActive])

  useGSAP(
    () => {
      const el = sectionRef.current
      if (!el) return

      const splits: SplitText[] = []

      const setup = () => {
        if (!el.isConnected) return

        const lines: Element[] = []
        const split = (selector: string) => {
          const target = el.querySelector<HTMLElement>(selector)
          if (!target) return
          const s = new SplitText(target, { type: "lines", linesClass: "split-line", mask: "lines" })
          splits.push(s)
          lines.push(...s.lines)
        }

        split("[data-text='label']")
        split("[data-text='desc']")

        const nameOutline = el.querySelector<SVGTextElement>("[data-home-name-outline]")
        const nameFill = el.querySelector<SVGTextElement>("[data-home-name-fill]")
        const tagRows = el.querySelectorAll("[data-home-marquee-row]")

        applyMarqueeRowTransforms(tagRows)
        gsap.set(lines, { yPercent: 105 })
        gsap.set(tagRows, { opacity: 0, y: 18 })

        if (nameOutline) {
          const length = nameOutline.getComputedTextLength()
          gsap.set(nameOutline, {
            strokeDasharray: length,
            strokeDashoffset: length,
          })
        }

        if (nameFill) {
          gsap.set(nameFill, { opacity: 0, y: 16 })
        }

        if (isActiveRef.current) {
          const timeline = gsap.timeline()
          if (nameOutline) {
            timeline.to(nameOutline, {
              strokeDashoffset: 0,
              duration: 1.1,
              ease: "power2.out",
            })
          }
          if (nameFill) {
            timeline.to(nameFill, {
              opacity: 0.92,
              y: 0,
              duration: 0.55,
              ease: "power2.out",
            }, "-=0.45")
          }
          timeline
            .to(lines, { yPercent: 0, stagger: 0.05, duration: 0.36, ease: "power2.out" }, "-=0.25")
            .to(tagRows, {
              opacity: (index) => MARQUEE_FIELDS[index]?.opacity ?? 1,
              y: 0,
              stagger: 0.08,
              duration: 0.4,
              ease: "power2.out",
            }, "-=0.12")
        }
      }

      document.fonts.ready.then(() => requestAnimationFrame(setup))
      return () => splits.forEach((s) => s.revert())
    },
    { scope: sectionRef },
  )

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    if (prevActiveRef.current === isActive) return

    const wasActive = prevActiveRef.current
    prevActiveRef.current = isActive

    const splitLines = el.querySelectorAll<HTMLElement>(".split-line")
    const tagRows = el.querySelectorAll<HTMLElement>("[data-home-marquee-row]")
    const nameOutline = el.querySelector<SVGTextElement>("[data-home-name-outline]")
    const nameFill = el.querySelector<SVGTextElement>("[data-home-name-fill]")

    applyMarqueeRowTransforms(tagRows)
    gsap.killTweensOf(splitLines)
    gsap.killTweensOf(tagRows)
    if (nameOutline) gsap.killTweensOf(nameOutline)
    if (nameFill) gsap.killTweensOf(nameFill)

    if (isActive) {
      const timeline = gsap.timeline()
      if (nameOutline) {
        const length = nameOutline.getComputedTextLength()
        gsap.set(nameOutline, {
          strokeDasharray: length,
          strokeDashoffset: length,
        })
        timeline.to(nameOutline, {
          strokeDashoffset: 0,
          duration: 1.1,
          ease: "power2.out",
        })
      }
      if (nameFill) {
        gsap.set(nameFill, { opacity: 0, y: 16 })
        timeline.to(nameFill, {
          opacity: 0.92,
          y: 0,
          duration: 0.55,
          ease: "power2.out",
        }, "-=0.45")
      }
      timeline
        .to(splitLines, { yPercent: 0, stagger: 0.05, duration: 0.36, ease: "power2.out" }, "-=0.2")
        .to(tagRows, {
          opacity: (index) => MARQUEE_FIELDS[index]?.opacity ?? 1,
          y: 0,
          stagger: 0.08,
          duration: 0.4,
          ease: "power2.out",
        }, "-=0.12")
    } else if (wasActive !== null) {
      gsap.timeline()
        .to(splitLines, { yPercent: 105, stagger: { each: 0.018, from: "start" }, duration: 0.18, ease: "power2.in" })
        .to(tagRows, { opacity: 0, y: 10, stagger: { each: 0.04, from: "end" }, duration: 0.18, ease: "power2.in" }, "<")
        .to(nameFill, { opacity: 0, y: 10, duration: 0.18, ease: "power2.in" }, "<")
    }
  }, [isActive])

  return (
    <section
      ref={(node) => assignHomeSectionRef(node, sectionRef, ref)}
      data-section="home"
      className="relative flex min-h-screen flex-col overflow-x-hidden py-32"
    >
      <HomeMarqueeField rows={marqueeRows} />
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <HomeContent />
      </div>
      <ScrollIndicator />
    </section>
  )
})

function HomeContent() {
  return (
    <div data-home-content className="relative isolate overflow-visible">
      <div data-home-copy className="relative z-10 flex max-w-5xl flex-col gap-6 md:gap-8">
        <div className="overflow-hidden">
          <span className="sr-only">Full Stack Developer</span>
          <p
            data-text="label"
            aria-hidden="true"
            className="text-[11px] font-medium uppercase tracking-[0.24em] text-foreground/42"
          >
            Full Stack Developer
          </p>
        </div>

        <HeroName />

        <div className="max-w-xl overflow-hidden">
          <span className="sr-only">I build things for the web from concept to code. Interactive systems, visual interfaces, and products with a distinct point of view.</span>
          <p
            data-text="desc"
            aria-hidden="true"
            className="text-base leading-relaxed font-light text-foreground/72 md:text-lg"
          >
            I build things for the web from concept to code. Interactive systems,
            visual interfaces, and products with a distinct point of view.
          </p>
        </div>
      </div>
    </div>
  )
}

function HomeMarqueeField({ rows }: { rows: string[][] }) {
  return (
    <div
      data-home-marquee
      className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible"
    >
      {rows.map((row, rowIndex) => (
        <MarqueeRow
          key={rowIndex}
          config={MARQUEE_FIELDS[rowIndex]}
          items={row}
        />
      ))}
    </div>
  )
}

function assignHomeSectionRef(
  node: HTMLElement | null,
  localRef: MutableRefObject<HTMLElement | null>,
  forwardedRef: ForwardedRef<HTMLElement>,
) {
  localRef.current = node
  if (typeof forwardedRef === "function") {
    forwardedRef(node)
    return
  }
  if (forwardedRef) {
    forwardedRef.current = node
  }
}

function HeroName() {
  return (
    <div data-home-name className="relative">
      <svg
        data-home-name-svg
        viewBox="0 0 1200 240"
        className="h-auto w-full max-w-6xl overflow-visible"
        role="img"
        aria-label="Brian Clark"
      >
        <text
          data-home-name-fill
          x="0"
          y="172"
          fill="currentColor"
          className="text-[168px] text-foreground/90 md:text-[220px]"
          style={{ fontSize: "clamp(4.6rem, 15vw, 12rem)", letterSpacing: "-0.05em" }}
        >
          Brian Clark
        </text>
        <text
          data-home-name-outline
          x="0"
          y="172"
          fill="transparent"
          stroke="currentColor"
          strokeWidth="1.1"
          className="text-[168px] text-foreground/32 md:text-[220px]"
          style={{ fontSize: "clamp(4.6rem, 15vw, 12rem)", letterSpacing: "-0.05em" }}
        >
          Brian Clark
        </text>
      </svg>
    </div>
  )
}

function MarqueeRow({
  config,
  items,
}: {
  config: {
    top: string
    left?: string
    width: string
    rotate: string
    rotateY: string
    perspective: string
    depth: number
    depthTilt: number
    scale: number
    opacity: number
    blur: string
    duration: number
    chipHeight: string
    chipFont: string
    chipIcon: string
    direction: "forward" | "reverse"
  }
  items: string[]
}) {
  return (
    <div
      data-home-marquee-row={config.direction === "reverse" ? "reverse" : "forward"}
      className="absolute py-2"
      style={
        {
          top: config.top,
          left: config.left,
          width: config.width,
          "--marquee-depth": `${config.depth}px`,
          "--marquee-depth-tilt": `${config.depthTilt}deg`,
          "--marquee-depth-rise": "10px",
          "--marquee-chip-height": config.chipHeight,
          "--marquee-chip-font": config.chipFont,
          "--marquee-chip-icon": config.chipIcon,
        } as CSSProperties
      }
    >
      <div
        data-home-marquee-track
        style={{ "--marquee-duration": `${config.duration}s` } as CSSProperties}
      >
        {items.map((tech, index) => {
          const chipCount = Math.max(items.length - 1, 1)
          const progress = index / chipCount
          const centeredProgress = progress * 2 - 1
          const directionMultiplier = config.direction === "forward" ? 1 : -1
          const depthProgress = centeredProgress * directionMultiplier

          return (
          <div
            key={`${tech}-${index}`}
            data-home-marquee-chip
            className="flex items-center gap-2 border border-foreground/10 bg-background px-3 font-light tracking-[0.12em] text-foreground/60 uppercase"
            style={
              {
                "--marquee-chip-progress": depthProgress,
                "--marquee-chip-abs-progress": Math.abs(depthProgress),
              } as CSSProperties
            }
          >
            <div data-home-marquee-chip-icon className="flex items-center justify-center border border-foreground/10 bg-foreground/[0.03] p-1.5">
              {TECH_STACK_ICONS[tech]
                ? <img src={`/logos/tech/${TECH_STACK_ICONS[tech]}.svg`} alt={tech} className="size-full object-contain" />
                : <div className="size-full bg-foreground/10" />
              }
            </div>
            <span>{tech}</span>
          </div>
          )
        })}
      </div>
    </div>
  )
}

function ScrollIndicator() {
  const indicatorRef = useRef<HTMLDivElement>(null)
  const isActive = useSectionScrollStore(
    (s) => s.sections[s.activeSectionIndex]?.type === "home",
  )

  useEffect(() => {
    const el = indicatorRef.current
    if (!el) return
    gsap.to(el, {
      opacity: isActive ? 1 : 0,
      y: isActive ? 0 : 8,
      duration: 0.4,
      ease: "power2.out",
    })
  }, [isActive])

  useGSAP(() => {
    const el = indicatorRef.current
    if (!el) return
    gsap.set(el, { opacity: 0, y: 8 })
    gsap.to(el, { opacity: 1, y: 0, duration: 0.6, delay: 1.2, ease: "power2.out" })
  }, { scope: indicatorRef })

  return (
    <div
      ref={indicatorRef}
      data-scroll-indicator
      className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      style={{ opacity: 0 }}
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-foreground/50">
        Scroll
      </span>
      <div data-scroll-indicator-rule className="h-8 w-px bg-foreground/20" />
    </div>
  )
}
